import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import alarmService from '../../services/alarmService';
import geofenceService from '../../services/geofenceService';
import { formatToIST } from '../../utils/dataUtils';
import { machineLabel } from '../../utils/machineLabel';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    Chip,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TextField,
    MenuItem,
    Skeleton,
    IconButton,
    Stack,
    Avatar
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Warning as WarningIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Info as InfoIcon,
    Refresh as RefreshIcon,
    NotificationsActive as AlarmIcon,
    NotificationsNone as BellIcon
} from '@mui/icons-material';

// variant="service" (Service → Errors): no severity and no manual close —
// an alarm is Active while the machine still has it and Solved once it went
// away, as the machine itself reports (the server derives it). The legacy
// /alarms page keeps severity, its filter and the Close action.
const AlarmsPage = ({ variant } = {}) => {
    const serviceLens = variant === 'service';
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [alarms, setAlarms] = useState([]);
    const [filterSeverity, setFilterSeverity] = useState('all');
    const [filterStatus, setFilterStatus] = useState('all');        // service lens: 'all' | 'Active' | 'Solved'
    const [filterCategory, setFilterCategory] = useState('all');   // 'all' | 'device' (device-reported only)

    const fetchAlarms = useCallback(async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams(location.search);
            const filters = {};
            if (params.get('equipment_id')) filters.equipment_id = params.get('equipment_id');
            if (params.get('contract_id'))  filters.contract_id  = params.get('contract_id');
            if (params.get('status'))       filters.status       = params.get('status');
            if (params.get('category'))     filters.category     = params.get('category');

            const res = await geofenceService.listAlarms(filters);
            const rows = Array.isArray(res?.data) ? res.data : [];

            // Normalize to the shape this page already uses for rendering
            const normalized = rows.map(a => ({
                id: a.id,
                severity: a.severity || 'warning',
                title: a.alarm_name,
                message: a.message || (a.alarm_name === 'GEOFENCE_EXIT'
                    ? `Machine ${machineLabel(a)} left site geofence`
                    : ''),
                // Machines are named by their serial (chassis) number; the
                // equipment id is kept for search and drill-down links.
                device: machineLabel(a, 'N/A'),
                equipment_id: a.equipment_id,
                contract_id: a.contract_id,
                time: a.created_at ? formatToIST(a.created_at, 'dd MMM, hh:mm:ss a') : '-',
                status: a.alarm_status === 'OPEN' ? 'Active' : (serviceLens ? 'Solved' : 'Resolved'),
                category: a.category,
                raw: a
            }));
            setAlarms(normalized);
        } catch (err) {
            console.error('Failed to fetch alarms', err);
            setAlarms([]);
        } finally {
            setLoading(false);
        }
    }, [location.search, serviceLens]);

    // Close an alarm (acknowledge)
    const handleClose = async (alarmId) => {
        try {
            await geofenceService.closeAlarm(alarmId);
            fetchAlarms();
        } catch (err) {
            console.error('Failed to close alarm', err);
        }
    };

    useEffect(() => {
        fetchAlarms();
    }, [fetchAlarms]);

    // Service lens: clicking an alarm opens THAT machine's Visualization at the
    // moment it happened — the /mewp-detail deep link the Service Control Panel
    // and the Daily Report use (?view=viz&date&at&alarm: the day loads, the
    // frame nearest the timestamp is selected, a banner names the alarm).
    const openViz = (row) => {
        if (!row.equipment_id) return;
        const t = row.raw?.created_at ? new Date(row.raw.created_at) : null;
        if (!t || isNaN(t)) { navigate(`/mewp-detail/${encodeURIComponent(row.equipment_id)}`); return; }
        const day = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
        const q = new URLSearchParams({ view: 'viz', date: day, at: t.toISOString() });
        if (row.title) q.set('alarm', row.title);
        navigate(`/mewp-detail/${encodeURIComponent(row.equipment_id)}?${q.toString()}`);
    };
    const toggleStatus = (s) => setFilterStatus(f => (f === s ? 'all' : s));
    const toggleDevice = () => setFilterCategory(c => (c === 'device' ? 'all' : 'device'));

    const isCritical = s => s === 'critical' || s === 'error';
    const isDeviceReported = (a) => a.category === 'DEVICE_ALERT' || a.category === 'DEVICE_DTC';
    // Theft alert: the device reported its power was cut. Emergency-red, distinct
    // from ordinary device alerts.
    const isPowerLoss = (a) => /power[\s_-]*(off|disconnect|cut|lost|fail|down)/i.test(`${a.title || ''} ${a.message || ''}`);
    const stats = {
        total: alarms.length,
        critical: alarms.filter(a => isCritical(a.severity)).length,
        warning: alarms.filter(a => a.severity === 'warning').length,
        resolved: alarms.filter(a => a.status !== 'Active').length,
        active: alarms.filter(a => a.status === 'Active').length,
        deviceReported: alarms.filter(isDeviceReported).length,
    };

    const filteredAlarms = alarms
        .filter(a => serviceLens
            ? filterStatus === 'all' || a.status === filterStatus
            : filterSeverity === 'all' || (filterSeverity === 'error' ? isCritical(a.severity) : a.severity === filterSeverity))
        .filter(a => filterCategory !== 'device' || isDeviceReported(a));

    return (
        <Box p={3} className="animate-fade-in">
            {/* Actions — no page title, the top bar already names the page */}
            <Box display="flex" justifyContent="flex-end" alignItems="center" mb={3}>
                <Stack direction="row" spacing={2}>
                    <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchAlarms} sx={{ borderRadius: '12px' }}>Refresh</Button>
                    <Button variant="contained" className="gradient-primary" startIcon={<ArrowBackIcon />} onClick={() => navigate('/')}>Dashboard</Button>
                </Stack>
            </Box>

            {/* Stats Grid — a card with an onClick filters the list (click again
                to clear): "Device Reported" isolates MQTT-native alerts/DTCs
                from the (usually much higher-volume) computed ones; on the
                service lens Active / Solved show only those, Total shows all. */}
            <Grid container spacing={3} mb={5}>
                {(serviceLens ? [
                    { label: 'Total Alerts', value: stats.total, icon: <BellIcon />, color: '#3b82f6',
                      onClick: () => { setFilterStatus('all'); setFilterCategory('all'); } },
                    { label: 'Active', value: stats.active, icon: <ErrorIcon />, color: '#ef4444',
                      onClick: () => toggleStatus('Active'), selected: filterStatus === 'Active' },
                    { label: 'Solved', value: stats.resolved, icon: <CheckCircleIcon />, color: '#10b981',
                      onClick: () => toggleStatus('Solved'), selected: filterStatus === 'Solved' },
                    { label: 'Device Reported', value: stats.deviceReported, icon: <AlarmIcon />, color: '#7c3aed',
                      onClick: toggleDevice, selected: filterCategory === 'device' },
                ] : [
                    { label: 'Total Alerts', value: stats.total, icon: <BellIcon />, color: '#3b82f6' },
                    { label: 'Critical Errors', value: stats.critical, icon: <ErrorIcon />, color: '#ef4444' },
                    { label: 'Warnings', value: stats.warning, icon: <WarningIcon />, color: '#f59e0b' },
                    { label: 'Resolved Issues', value: stats.resolved, icon: <CheckCircleIcon />, color: '#10b981' },
                    { label: 'Device Reported', value: stats.deviceReported, icon: <AlarmIcon />, color: '#7c3aed',
                      onClick: toggleDevice, selected: filterCategory === 'device' },
                ]).map((stat, i) => (
                    <Grid item xs={12} sm={6} md={serviceLens ? 3 : 2.4} key={i}>
                        <Card
                            onClick={stat.onClick}
                            {...(stat.onClick ? {
                                role: 'button', tabIndex: 0, 'aria-pressed': !!stat.selected, 'aria-label': `${stat.label}: ${stat.value}`,
                                onKeyDown: (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); stat.onClick(); } },
                            } : {})}
                            sx={{
                                height: '100%', borderBottom: `4px solid ${stat.color}`,
                                cursor: stat.onClick ? 'pointer' : 'default',
                                boxShadow: stat.selected ? `0 0 0 2px ${stat.color}` : undefined,
                                transition: 'box-shadow .15s',
                            }}
                        >
                            <CardContent>
                                <Box display="flex" justifyContent="space-between" mb={2}>
                                    <Avatar sx={{ bgcolor: `${stat.color}15`, color: stat.color, borderRadius: '12px' }}>{stat.icon}</Avatar>
                                    <Typography variant="h3" sx={{ fontWeight: 800 }}>{loading ? <Skeleton width={40} /> : stat.value}</Typography>
                                </Box>
                                <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase' }}>{stat.label}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Alarm List */}
            <Stack spacing={3}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>Alert History</Typography>
                    {serviceLens ? (
                        <TextField
                            select
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                            size="small"
                            inputProps={{ 'aria-label': 'Filter by status' }}
                            sx={{ minWidth: 180, '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'white' } }}
                        >
                            <MenuItem value="all">All Statuses</MenuItem>
                            <MenuItem value="Active">Active</MenuItem>
                            <MenuItem value="Solved">Solved</MenuItem>
                        </TextField>
                    ) : (
                        <TextField
                            select
                            value={filterSeverity}
                            onChange={(e) => setFilterSeverity(e.target.value)}
                            size="small"
                            sx={{ minWidth: 180, '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'white' } }}
                        >
                            <MenuItem value="all">All Severities</MenuItem>
                            <MenuItem value="error">Critical</MenuItem>
                            <MenuItem value="warning">Warning</MenuItem>
                            <MenuItem value="info">Information</MenuItem>
                        </TextField>
                    )}
                </Box>

                <TableContainer component={Card} sx={{ borderRadius: '20px' }}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: 'rgba(248, 250, 252, 0.5)' }}>
                                {!serviceLens && <TableCell sx={{ fontWeight: 700 }}>Severity</TableCell>}
                                <TableCell sx={{ fontWeight: 700 }}>Serial number</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Time</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 700 }}>Status</TableCell>
                                {!serviceLens && <TableCell align="right" sx={{ fontWeight: 700 }}>Action</TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? [...Array(4)].map((_, i) => (
                                <TableRow key={i}><TableCell colSpan={serviceLens ? 4 : 6}><Skeleton height={40} sx={{ my: 1 }} /></TableCell></TableRow>
                            )) : filteredAlarms.length === 0 ? (
                                <TableRow><TableCell colSpan={serviceLens ? 4 : 6} align="center" sx={{ py: 5, color: 'text.secondary', fontWeight: 600 }}>
                                    No alarms match this filter
                                </TableCell></TableRow>
                            ) : filteredAlarms.map((row) => {
                                const deviceReported = isDeviceReported(row);
                                const powerLoss = isPowerLoss(row);
                                const categoryLabel = row.category === 'DEVICE_ALERT' ? 'Device Alert'
                                    : row.category === 'DEVICE_DTC' ? 'Device DTC' : row.category;
                                return (
                                <TableRow key={row.id} hover
                                    {...(serviceLens ? {
                                        onClick: () => openViz(row), tabIndex: 0,
                                        'aria-label': `${row.device} · ${row.title} · ${row.time} — open in the visualization`,
                                        onKeyDown: (e) => { if (e.key === 'Enter') openViz(row); },
                                    } : {})}
                                    sx={{
                                        ...(serviceLens ? { cursor: 'pointer' } : {}),
                                        ...(powerLoss
                                            ? { borderLeft: '4px solid #dc2626', bgcolor: 'rgba(220,38,38,0.07)' }
                                            : deviceReported ? { borderLeft: '3px solid #7c3aed', bgcolor: 'rgba(124,58,237,0.04)' } : {}),
                                    }}>
                                    {!serviceLens && (
                                    <TableCell>
                                        <Chip
                                            label={powerLoss ? '🚨 EMERGENCY' : (row.severity || '').toUpperCase()}
                                            size="small"
                                            color={powerLoss || isCritical(row.severity) ? 'error' : row.severity === 'warning' ? 'warning' : 'info'}
                                            sx={{ fontWeight: 900, height: 18, fontSize: '10px' }}
                                        />
                                    </TableCell>
                                    )}
                                    <TableCell sx={{ fontWeight: 800, color: 'primary.main' }}>
                                        {row.device || 'N/A'}
                                        {row.contract_id && (
                                            <Typography variant="caption" display="block" color="text.secondary" sx={{ fontWeight: 600 }}>
                                                {row.contract_id}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="subtitle2" sx={{ fontWeight: powerLoss ? 900 : 700, color: powerLoss ? '#b91c1c' : 'inherit' }}>
                                            {powerLoss ? '⚡ Power Disconnected' : row.title}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">{row.message}</Typography>
                                        {powerLoss ? (
                                            <Chip label="POWER DISCONNECTED · POSSIBLE THEFT" size="small"
                                                  sx={{ ml: 1, height: 18, fontSize: '9px', fontWeight: 900, bgcolor: '#dc2626', color: '#fff' }} />
                                        ) : row.category && (
                                            deviceReported ? (
                                                <Chip label={`DEVICE REPORTED · ${categoryLabel}`} size="small"
                                                      sx={{ ml: 1, height: 18, fontSize: '9px', fontWeight: 800, bgcolor: '#7c3aed', color: '#fff' }} />
                                            ) : (
                                                <Chip label={categoryLabel} size="small" variant="outlined"
                                                      sx={{ ml: 1, height: 16, fontSize: '9px', fontWeight: 700 }} />
                                            )
                                        )}
                                    </TableCell>
                                    <TableCell><Typography variant="body2" sx={{ fontWeight: 500 }}>{row.time}</Typography></TableCell>
                                    <TableCell align="center">
                                        <Chip
                                            label={row.status}
                                            variant={row.status === 'Active' ? 'contained' : 'outlined'}
                                            color={row.status === 'Active' ? 'error' : 'success'}
                                            size="small"
                                            sx={{ fontWeight: 800, borderRadius: '6px' }}
                                        />
                                    </TableCell>
                                    {!serviceLens && (
                                    <TableCell align="right">
                                        {deviceReported ? (
                                            <Typography variant="caption" color="text.disabled">Device-reported</Typography>
                                        ) : row.status === 'Active' ? (
                                            <Button size="small" variant="outlined" color="success"
                                                    onClick={() => handleClose(row.id)}
                                                    sx={{ fontWeight: 700, borderRadius: '8px' }}>
                                                Close
                                            </Button>
                                        ) : (
                                            <Button size="small" sx={{ fontWeight: 700 }} disabled>Closed</Button>
                                        )}
                                    </TableCell>
                                    )}
                                </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Stack>
        </Box>
    );
};

export default AlarmsPage;
